import { MouseEvent } from 'react';
import { CloseIcon } from '@icons';
import { Badge } from '@ds';
import { useDraggableReorder } from '@/hooks/useDraggableReorder';
import { OrganType } from '@/api/apis/case';
import { getColorsByOrganType, ORGAN_ICON } from '@/utils/helpers';

import styles from './CaseTab.module.scss';

type CaseTabType = {
  id: string;
  donorId: string;
  caseSuffixes: string;
  organType: OrganType;
  unreadCount?: number;
  checkCurrentCase: (id: string) => boolean;
  onClick: (id: string) => void;
  onRemove: (id: string) => void;
  onReorder: (draggedId: string, targetId: string) => void;
};

export const CaseTab = ({
  id,
  organType,
  donorId,
  caseSuffixes,
  unreadCount = 0,
  checkCurrentCase,
  onRemove,
  onClick,
  onReorder,
}: CaseTabType) => {
  const OrganIcon = ORGAN_ICON[organType];
  const isCurrent = checkCurrentCase(id);
  const colors = getColorsByOrganType(organType);

  const { isDragOver, draggableProps } = useDraggableReorder({
    id,
    onReorder,
  });

  const handleRemove = (e: MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();

    onRemove(id);
  };

  return (
    <button
      type="button"
      className={`
        ${styles.caseTab}
        cursor-pointer group flex shrink-0 py-1.5 px-2 items-center gap-2
        transition-transform
        ${isDragOver ? 'scale-[1.03] ring-1 ring-base-20' : ''}
        ${
          isCurrent
            ? `${styles.isCurrent} is-current rounded-lg bg-base-5 text-base-90`
            : 'text-base-20 bg-transparent hover:text-base-5'
        }
      `}
      onClick={() => onClick(id)}
      {...draggableProps}
    >
      <Badge
        content={unreadCount > 99 ? '99+' : unreadCount}
        isInvisible={!unreadCount}
        size="sm"
        classNames={{
          badge: 'bg-red border-base-60 text-white min-w-4 min-h-4 h-4 w-auto px-1 text-[10px] leading-none',
        }}
      >
        <div
          className={`${styles[organType]} flex justify-center items-center rounded-full bg-base-50 text-black group-hover:text-base-5 group-[.is-current]:text-base-5 w-6 h-6`}
        >
          <OrganIcon />
        </div>
      </Badge>

      <div className="text-m-m leading-none flex gap-1">
        <span>{donorId}</span>
        <span className={colors.textDefault}>{caseSuffixes}</span>
      </div>

      <CloseIcon className="opacity-0 group-hover:opacity-100 group-[.is-current]:opacity-100" onClick={handleRemove} />
    </button>
  );
};
